import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';

const AllBusinessProfilesPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch businesses from cache with pagination support
  const loadBusinesses = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        setError('Authentifizierung erforderlich');
        setLoading(false);
        return;
      }

      // Build URL with pagination and search params
      const params = new URLSearchParams({
        page: page.toString(),
        limit: itemsPerPage.toString(),
        ...(search && { search })
      });

      // Fetch cached Geschäftsprofile data with backend pagination
      const response = await fetch(`/api/geschaeftsprofile-cache?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        console.log('📊 Cached profile data loaded:', data.data.length, 'of', data.pagination?.total);
        setBusinesses(data.data || []);
        setTotalItems(data.pagination?.total || 0);
        setCurrentPage(page);
      } else {
        setError(data.error || 'Fehler beim Laden der Geschäftsprofile');
      }
    } catch (err) {
      console.error('Error loading businesses:', err);
      setError(err.message || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBusinesses(1, '');
  }, []);

  // Calculate pagination info based on backend data
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  // Format count display
  const formatCount = (count) => count || 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
              variant="h4"
              sx={{
                mb: 0.5,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: amDesignColors.primary,
                fontFamily: amDesignTypography.fontFamily.heading
              }}
            >  Portfolio</Typography>
        <Typography variant="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Alle Geschäftsprofile
        </Typography>
        <Typography
              variant="body2"
              sx={{
                color: amDesignColors.text.secondary,
                fontFamily: amDesignTypography.fontFamily.body
              }}
            >
          Übersicht aller Geschäftsprofile mit Details zu Posts, Medien, Services und Keywords
        </Typography>
      </Box>

      {/* Search and Controls */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Nach Name oder Adresse suchen..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => {
            const newSearch = e.target.value;
            setSearchTerm(newSearch);
            loadBusinesses(1, newSearch);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchTerm('');
                    loadBusinesses(1, '');
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{ flex: 1, maxWidth: 400 }}
        />
        <Tooltip title="Neu laden">
          <IconButton onClick={loadBusinesses} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* No Data State */}
      {!loading && totalItems === 0 && (
        <Alert severity="info">
          Keine Geschäftsprofile gefunden
        </Alert>
      )}

      {/* Table */}
      {!loading && totalItems > 0 && (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: amDesignColors.paper }}>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Posts</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Medien</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Services</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Keywords</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Aktion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {businesses.map((business) => (
                  <TableRow
                    key={business._id || business.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {business.businessName || 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {business.address || 'Keine Adresse'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={formatCount(business.postsCount || 0)}
                        size="small"
                        variant="outlined"
                        color={business.postsCount > 0 ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={formatCount(business.mediasCount || 0)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={formatCount(business.servicesCount || 0)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={formatCount(business.keywordsCount || 0)}
                        size="small"
                        color={business.keywordsCount > 0 ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Öffnen">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const locId = business.locationId;
                            if (locId && locId !== 'N/A') {
                              navigate(`/profile/${locId}`);
                            }
                          }}
                        >
                          <OpenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => {
                  loadBusinesses(page, searchTerm);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                color="primary"
              />
            </Box>
          )}

          {/* Info Text */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Zeige {businesses.length > 0 ? startIndex + 1 : 0} bis {endIndex} von {totalItems} Einträgen
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AllBusinessProfilesPage;
